import { Component, type ReactNode } from 'react'
import i18n from '../i18n'

interface Props {
  children: ReactNode
  /** When this value changes, a tripped boundary auto-resets (e.g. on route change). */
  resetKey?: string
}
interface State {
  error: Error | null
}

/**
 * Last-resort UI guard: if any descendant throws during render, show a friendly fallback
 * instead of a white screen — on every device. Copy is hardcoded (Italian) so the fallback
 * never itself depends on i18n/state that may be the thing that failed.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  componentDidCatch(error: unknown) {
    try {
      // eslint-disable-next-line no-console
      console.error('[GSM] UI error:', error)
    } catch {
      /* never throw from the handler */
    }
  }

  render() {
    if (!this.state.error) return this.props.children
    const t = i18n.t.bind(i18n)
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-ink-50 px-6 text-center">
        <img src="/brand/logo.svg" alt={t('brand.name')} className="h-10 w-auto" />
        <div className="max-w-sm">
          <h1 className="font-display text-xl font-extrabold text-ink-900">{t('errors.title')}</h1>
          <p className="mt-2 text-sm leading-relaxed text-ink-500">
            {t('errors.text')}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => this.setState({ error: null })}
            className="rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-brand-700"
          >
            {t('common.retry')}
          </button>
          <a
            href="/"
            className="rounded-xl border border-ink-200 bg-white px-5 py-2.5 text-sm font-semibold text-ink-700 transition hover:bg-ink-50"
          >
            {t('common.backHome')}
          </a>
        </div>
      </div>
    )
  }
}
